import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowLeft, CheckCircle2, PlusCircle, Send, BarChart3, Megaphone, UserPlus, Activity, Trophy, Medal, Flame, Award, Lock, CalendarClock } from "lucide-react";
import { BrutalCard, SectionTitle, EmptyState } from "@/components/Brutal";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";

const ICONS = {
  task_completed: { icon: CheckCircle2, color: "bg-brand-green" },
  task_created: { icon: PlusCircle, color: "bg-brand-blue text-white" },
  task_transferred: { icon: Send, color: "bg-brand-blue text-white" },
  task_postponed: { icon: CalendarClock, color: "bg-brand-yellow" },
  poll_created: { icon: BarChart3, color: "bg-brand-pink" },
  member_joined: { icon: UserPlus, color: "bg-brand-yellow" },
  points_adjust: { icon: Trophy, color: "bg-brand-violet text-white" },
  points_forgive: { icon: Medal, color: "bg-brand-green" },
};
const metaFor = (type) => ICONS[type] || (type?.startsWith("content_") ? { icon: Megaphone, color: "bg-brand-yellow" } : { icon: Activity, color: "bg-white" });

function relTime(iso, tr = (s) => s) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return tr("zojuist");
  if (s < 3600) return `${Math.floor(s / 60)} ${tr("min geleden")}`;
  if (s < 86400) return `${Math.floor(s / 3600)} ${tr("uur geleden")}`;
  if (s < 604800) return `${Math.floor(s / 86400)} ${tr("dag(en) geleden")}`;
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
}

export default function Tijdlijn() {
  const navigate = useNavigate();
  const t = useT();
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [group, setGroup] = useState("");

  const load = async (gid) => {
    const params = gid ? { group_id: gid } : {};
    const [a, s, h] = await Promise.all([
      api.get("/activity", { params }),
      api.get("/activity/stats", { params }),
      api.get("/activity/heatmap", { params: { ...params, days: 28 } }),
    ]);
    setItems(a.data); setStats(s.data); setHeatmap(h.data);
  };

  useEffect(() => { api.get("/groups").then((r) => setGroups(r.data)); api.get("/badges").then((r) => setBadges(r.data)); }, []);
  useEffect(() => { load(group); }, [group]);

  const medals = ["text-brand-gold", "text-brand-ink/60", "text-brand-orange"];

  return (
    <div className="space-y-5" data-testid="tijdlijn-page">
      <button onClick={() => navigate("/meer")} className="flex items-center gap-1.5 font-bold text-sm text-brand-ink/60"><ArrowLeft className="w-4 h-4" strokeWidth={2.5} />{t("Meer")}</button>
      <SectionTitle sub={t("Wie deed wat, en wanneer")}>{t("Tijdlijn")}</SectionTitle>

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        <button data-testid="tl-group-all" onClick={() => setGroup("")} className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold whitespace-nowrap ${!group ? "bg-brand-ink text-white" : "bg-white"}`}>{t("Alle groepen")}</button>
        {groups.map((g) => (
          <button key={g.id} data-testid={`tl-group-${g.id}`} onClick={() => setGroup(g.id)}
            className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold whitespace-nowrap ${group === g.id ? "text-white" : "bg-white"}`}
            style={group === g.id ? { backgroundColor: g.color } : {}}>{g.name}</button>
        ))}
      </div>

      {stats?.leaderboard?.length > 0 && (
        <BrutalCard color="blue" data-testid="tl-leaderboard">
          <div className="flex items-center gap-2 mb-3"><Trophy className="w-5 h-5" strokeWidth={2.5} /><h3 className="font-head font-bold text-lg">Ranglijst — afgeronde taken (30d)</h3></div>
          <div className="space-y-2">
            {stats.leaderboard.slice(0, 5).map((l, i) => (
              <div key={l.user_id} className="flex items-center gap-3 bg-white/15 rounded-xl px-3 py-2">
                <Medal className={`w-5 h-5 ${medals[i] || "text-white/70"}`} strokeWidth={2.5} />
                <span className="font-head font-bold flex-1">{l.name}</span>
                {l.streak > 0 && (
                  <span className="flex items-center gap-1 text-sm font-black bg-brand-orange text-white rounded-full px-2 py-0.5" title={t("Huidige streak")}>
                    <Flame className="w-4 h-4" strokeWidth={2.5} />{l.streak}
                  </span>
                )}
                <span className="brutal-sm bg-white text-brand-ink rounded-full px-2.5 py-0.5 text-sm font-black">{l.count}</span>
              </div>
            ))}
          </div>
        </BrutalCard>
      )}

      {badges && (
        <div data-testid="badges-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-head font-black text-xl flex items-center gap-2"><Award className="w-5 h-5" strokeWidth={2.5} />Mijn badges</h3>
            <span className="flex items-center gap-3 text-sm font-bold">
              <span className="flex items-center gap-1 text-brand-orange"><Flame className="w-4 h-4" strokeWidth={2.5} />{badges.current_streak} dag(en)</span>
              <span className="brutal-sm bg-white rounded-full px-2.5 py-0.5">{badges.earned}/{badges.badges.length}</span>
            </span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
            {badges.badges.map((b) => {
              const Ic = Icons[b.icon] || Award;
              return (
                <div key={b.id} data-testid={`badge-${b.id}`}
                  className={`brutal-sm rounded-xl p-3 flex flex-col items-center text-center gap-1 ${b.earned ? "bg-brand-yellow" : "bg-white opacity-70"}`}>
                  <div className={`rounded-full p-2 ${b.earned ? "azure-gradient text-white" : "bg-brand-cream"}`}>
                    {b.earned ? <Ic className="w-6 h-6" strokeWidth={2.5} /> : <Lock className="w-6 h-6" strokeWidth={2.5} />}
                  </div>
                  <p className="font-head font-bold text-xs leading-tight">{b.name}</p>
                  <p className="text-[10px] text-brand-ink/50 leading-tight">{b.earned ? t("Behaald!") : `${b.current}/${b.target}`}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {heatmap?.users?.length > 0 && (() => {
        const maxC = Math.max(1, ...heatmap.users.flatMap((u) => u.counts));
        const cell = (c) => c === 0
          ? { backgroundColor: "transparent", borderColor: "rgba(27,20,64,0.15)" }
          : { backgroundColor: `rgba(139,92,255,${0.25 + 0.75 * (c / maxC)})`, borderColor: "#1B1440" };
        const labels = heatmap.days.map((d) => new Date(d).getDate());
        return (
          <div data-testid="heatmap-section">
            <h3 className="font-head font-black text-xl flex items-center gap-2 mb-3"><Icons.CalendarRange className="w-5 h-5" strokeWidth={2.5} />{t("Weekoverzicht (28 dagen)")}</h3>
            <BrutalCard className="overflow-x-auto no-scrollbar">
              <div className="min-w-max space-y-2">
                {heatmap.users.map((u) => (
                  <div key={u.user_id} data-testid={`heatmap-${u.user_id}`} className="flex items-center gap-2">
                    <span className="font-head font-bold text-sm w-24 truncate shrink-0">{u.name}</span>
                    <div className="flex gap-1">
                      {u.counts.map((c, i) => (
                        <div key={i} title={`${labels[i]}: ${c}`} className="w-4 h-4 rounded-[3px] border" style={cell(c)} />
                      ))}
                    </div>
                    <span className="ml-2 text-xs font-bold text-brand-ink/50 shrink-0">{u.total}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-1.5 mt-3 text-xs font-semibold text-brand-ink/50">
                <span>{t("minder")}</span>
                {[0, 0.4, 0.7, 1].map((a, i) => <div key={i} className="w-3.5 h-3.5 rounded-[3px] border border-brand-ink" style={{ backgroundColor: a === 0 ? "transparent" : `rgba(46,139,255,${0.25 + 0.75 * a})` }} />)}
                <span>{t("meer")}</span>
              </div>
            </BrutalCard>
          </div>
        );
      })()}

      {items.length === 0 ? <EmptyState icon={Activity} title={t("Nog geen activiteit")} text={t("Zodra er taken worden gedaan verschijnt het hier.")} /> : (
        <div className="relative pl-4">
          <div className="absolute left-[22px] top-2 bottom-2 w-0.5 bg-brand-ink/15" />
          <div className="space-y-3">
            {items.map((it) => {
              const m = metaFor(it.type);
              return (
                <div key={it.id} data-testid={`activity-${it.id}`} className="flex items-start gap-3 relative">
                  <div className={`z-10 shrink-0 brutal-sm rounded-full w-9 h-9 flex items-center justify-center ${m.color}`}>
                    <m.icon className="w-4 h-4" strokeWidth={2.5} />
                  </div>
                  <div className="brutal-sm rounded-xl bg-white px-3 py-2 flex-1">
                    <p className="text-sm font-semibold"><b className="font-head">{it.actor_name}</b> {it.text}</p>
                    <p className="text-xs text-brand-ink/40 font-semibold">{relTime(it.created_at, t)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
